import { invoke } from '@tauri-apps/api/core';
import { resetExternalSqlStorageCache } from './adapters/externalSqlStorageAdapter';
import { externalSqlConnectionPayload, loadEnvironmentSettings, validateDatabaseSettings } from './environmentSettings';
import { getProductModeInfo } from './productMode';

const PREFIX = 'revenue:';
const STORAGE_SCHEMA_KEY = `${PREFIX}schema_version`;
const SQLITE_MIGRATION_MARKER_KEY = `${PREFIX}sqlite_migration_v1`;
const CURRENT_STORAGE_SCHEMA_VERSION = 2;
const cache = new Map<string, string>();
let initialized = false;
let sqliteWriteQueue = Promise.resolve();
let sqliteWriteError: unknown = null;
let storageInitializationError: string | null = null;
export const STORAGE_WRITE_ERROR_EVENT = 'revenue:storage-write-error';

function isTauriRuntime(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

function useSqliteStorage(): boolean {
  return isTauriRuntime() && getProductModeInfo().mode === 'standalone';
}

function useExternalSqlStorage(): boolean {
  return isTauriRuntime() && getProductModeInfo().mode === 'server-client';
}

function useDesktopStorage(): boolean {
  return useSqliteStorage() || useExternalSqlStorage();
}

function storageKindLabel(): string {
  return useExternalSqlStorage() ? '外部SQL' : 'SQLite';
}

function externalSqlSettingsOrThrow() {
  const database = loadEnvironmentSettings().database;
  const error = validateDatabaseSettings(database);
  if (error) throw new Error(error);
  return externalSqlConnectionPayload(database);
}

function storageSaveCommandPayload(key: string, value: string): { command: string; payload: Record<string, unknown> } {
  if (useExternalSqlStorage()) {
    return {
      command: 'external_sql_storage_save_value',
      payload: { settings: externalSqlSettingsOrThrow(), key, value },
    };
  }
  return {
    command: 'storage_save_value',
    payload: { key, value },
  };
}

function storageDeletePrefixCommandPayload(prefix: string): { command: string; payload: Record<string, unknown> } {
  if (useExternalSqlStorage()) {
    return {
      command: 'external_sql_storage_delete_prefix',
      payload: { settings: externalSqlSettingsOrThrow(), prefix },
    };
  }
  return {
    command: 'storage_delete_prefix',
    payload: { prefix },
  };
}

function readRaw(key: string): string | null {
  if (useDesktopStorage()) {
    return cache.get(key) ?? null;
  }
  return localStorage.getItem(key);
}

function writeRaw(key: string, value: string): void {
  if (storageInitializationError) {
    throw new Error(storageInitializationError);
  }
  if (useDesktopStorage()) {
    cache.set(key, value);
    const { command, payload } = storageSaveCommandPayload(key, value);
    sqliteWriteQueue = sqliteWriteQueue
      .then(() => invoke(command, payload))
      .then(() => undefined)
      .catch(error => {
        sqliteWriteError = error;
        console.error(`${storageKindLabel()}保存に失敗しました`, error);
        dispatchStorageWriteError(error);
      });
    return;
  }
  localStorage.setItem(key, value);
}

function removeRevenueKeys(): void {
  if (storageInitializationError) {
    throw new Error(storageInitializationError);
  }
  if (useDesktopStorage()) {
    cache.clear();
    if (useExternalSqlStorage()) resetExternalSqlStorageCache();
    const { command, payload } = storageDeletePrefixCommandPayload(PREFIX);
    sqliteWriteQueue = sqliteWriteQueue
      .then(() => invoke(command, payload))
      .then(() => undefined)
      .catch(error => {
        sqliteWriteError = error;
        console.error(`${storageKindLabel()}データ削除に失敗しました`, error);
        dispatchStorageWriteError(error);
      });
    return;
  }

  const existingKeys = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(PREFIX)) existingKeys.push(key);
  }
  for (const key of existingKeys) localStorage.removeItem(key);
}

function dispatchStorageWriteError(error: unknown): void {
  if (typeof window === 'undefined') return;
  const message = error instanceof Error ? error.message : String(error);
  window.dispatchEvent(new CustomEvent(STORAGE_WRITE_ERROR_EVENT, {
    detail: { message: `${storageKindLabel()}保存に失敗しました: ${message}` },
  }));
}

function revenueKeys(): string[] {
  if (useDesktopStorage()) {
    return Array.from(cache.keys()).filter(key => key.startsWith(PREFIX));
  }
  const keys: string[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

function snapshotRevenueRawValues(): Record<string, string> {
  const snapshot: Record<string, string> = {};
  for (const key of revenueKeys()) {
    const raw = readRaw(key);
    if (raw !== null) snapshot[key] = raw;
  }
  return snapshot;
}

function restoreRevenueRawValues(snapshot: Record<string, string>): void {
  removeRevenueKeys();
  for (const [key, value] of Object.entries(snapshot)) {
    writeRaw(key, value);
  }
}

function localStorageRevenueKeys(): string[] {
  const keys: string[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

async function flushStorageWrites(): Promise<void> {
  await sqliteWriteQueue;
  if (sqliteWriteError) {
    const message = sqliteWriteError instanceof Error ? sqliteWriteError.message : String(sqliteWriteError);
    sqliteWriteError = null;
    throw new Error(`${storageKindLabel()}保存に失敗しました: ${message}`);
  }
}

function isRustLicenseStorageError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('ライセンス認証済みでないためSQLite保存は利用できません')
    || message.includes('ライセンス端末情報が現在のPCと一致しません')
    || message.includes('ライセンス状態のチェックサム不一致');
}

async function flushInitialStorageWrites(): Promise<void> {
  try {
    await flushStorageWrites();
  } catch (error) {
    if (!isRustLicenseStorageError(error)) throw error;
    console.warn('Rust側ライセンス未同期のため、起動時SQLite移行の永続化を延期しました', error);
  }
}

function migrateBrowserStorageToSqlite(): number {
  if (cache.has(SQLITE_MIGRATION_MARKER_KEY)) return 0;
  let migrated = 0;
  for (const key of localStorageRevenueKeys()) {
    if (key === SQLITE_MIGRATION_MARKER_KEY || cache.has(key)) continue;
    const raw = localStorage.getItem(key);
    if (raw === null) continue;
    writeRaw(key, raw);
    migrated += 1;
  }
  writeRaw(SQLITE_MIGRATION_MARKER_KEY, JSON.stringify({
    migrated_at: now(),
    migrated_keys: migrated,
  }));
  return migrated;
}

export interface RevenueStorageBackup {
  app: 'revenue';
  schema_version: number;
  exported_at: string;
  keys: Record<string, unknown>;
}

export function load<T>(key: string): T[] {
  try {
    const raw = readRaw(PREFIX + key);
    return raw ? (JSON.parse(raw) as T[]) : [];
  } catch {
    return [];
  }
}

export function save<T>(key: string, data: T[]): void {
  writeRaw(PREFIX + key, JSON.stringify(data));
}

export function loadOne<T>(key: string): T | null {
  try {
    const raw = readRaw(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function saveOne<T>(key: string, data: T): void {
  writeRaw(PREFIX + key, JSON.stringify(data));
}

export function upsertMany<T extends { id: string }>(key: string, items: T[]): void {
  const map = new Map(load<T>(key).map(item => [item.id, item]));
  for (const item of items) map.set(item.id, item);
  save(key, Array.from(map.values()));
}

export function uid(): string {
  return crypto.randomUUID();
}

export function now(): string {
  return new Date().toISOString();
}

function readStorageSchemaVersion(): number {
  const raw = readRaw(STORAGE_SCHEMA_KEY);
  const version = raw ? Number(raw) : 0;
  return Number.isInteger(version) && version >= 0 ? version : 0;
}

function migrateTransactionDescriptions(): void {
  const incomes = load<Record<string, unknown>>('incomes');
  save('incomes', incomes.map(income => ({
    ...income,
    description: typeof income.description === 'string'
      ? income.description
      : typeof income.notes === 'string'
      ? income.notes
      : '',
  })));

  const expenses = load<Record<string, unknown>>('expenses');
  save('expenses', expenses.map(expense => {
    const description = typeof expense.description === 'string'
      ? expense.description
      : typeof expense.content === 'string'
      ? expense.content
      : '';
    return {
      ...expense,
      description,
      content: typeof expense.content === 'string' ? expense.content : description,
    };
  }));
}

export function migrateStorage(): void {
  if (storageInitializationError) return;
  if (useDesktopStorage() && !initialized) {
    throw new Error(`${storageKindLabel()}ストレージの初期化前にデータへアクセスしようとしました`);
  }
  const fromVersion = readStorageSchemaVersion();
  if (fromVersion >= CURRENT_STORAGE_SCHEMA_VERSION) return;

  const snapshot = snapshotRevenueRawValues();
  try {
    if (fromVersion < 2) {
      migrateTransactionDescriptions();
    }

    writeRaw(STORAGE_SCHEMA_KEY, String(CURRENT_STORAGE_SCHEMA_VERSION));
  } catch (error) {
    restoreRevenueRawValues(snapshot);
    throw error;
  }
}

export async function initializeStorage(): Promise<void> {
  if (initialized) return;
  initialized = true;
  if (!useDesktopStorage()) {
    migrateStorage();
    return;
  }

  const values = await (async () => {
    try {
      if (useExternalSqlStorage()) {
        const database = loadEnvironmentSettings().database;
        const error = validateDatabaseSettings(database);
        if (error) {
          storageInitializationError = error;
          return {};
        }
        return await invoke<Record<string, string>>('external_sql_storage_load_all', {
          settings: externalSqlConnectionPayload(database),
        });
      }
      return await invoke<Record<string, string>>('storage_load_all');
    } catch (error) {
      if (useExternalSqlStorage()) {
        storageInitializationError = error instanceof Error ? error.message : String(error);
        return {};
      }
      throw error;
    }
  })();
  cache.clear();
  for (const [key, value] of Object.entries(values)) {
    if (key.startsWith(PREFIX)) cache.set(key, value);
  }

  if (useSqliteStorage()) migrateBrowserStorageToSqlite();
  migrateStorage();
  await flushInitialStorageWrites();
}

export { flushStorageWrites };

export function getStorageInitializationError(): string | null {
  return storageInitializationError;
}

export function exportRevenueStorage(): RevenueStorageBackup {
  const keys: Record<string, unknown> = {};
  for (const key of revenueKeys()) {
    const raw = readRaw(key);
    if (raw === null) continue;
    try {
      keys[key] = JSON.parse(raw);
    } catch {
      keys[key] = raw;
    }
  }
  return {
    app: 'revenue',
    schema_version: CURRENT_STORAGE_SCHEMA_VERSION,
    exported_at: now(),
    keys,
  };
}

export function importRevenueStorage(backup: unknown): number {
  if (!backup || typeof backup !== 'object') {
    throw new Error('バックアップJSONの形式が正しくありません');
  }
  const candidate = backup as Partial<RevenueStorageBackup>;
  if (candidate.app !== 'revenue' || !candidate.keys || typeof candidate.keys !== 'object') {
    throw new Error('収益分配システムのバックアップJSONではありません');
  }

  const entries = Object.entries(candidate.keys).filter(([key]) => key.startsWith(PREFIX));
  if (entries.length === 0) {
    throw new Error('復元できるデータが含まれていません');
  }

  removeRevenueKeys();
  for (const [key, value] of entries) {
    writeRaw(key, JSON.stringify(value));
  }
  migrateStorage();
  return entries.length;
}
